// Angular
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// RxJS
import { exhaustMap, Observable, switchMap } from 'rxjs';

// Data Contracts
import { ClerkReviewDocument, ClerkReviewFiling, ClerkReviewFilingConfiguration, ClerkReviewListResponse, ClerkReviewQueryRequest, ClerkReviewParty, ClerkReviewRecipientsResponse, EscalateClerkReviewFilingRequest, SortOrder, SubmitClerkReviewResult } from '@data-contracts';

// Local
import { ENV_CONFIG } from '@app/core/app-config/app-config.service';
import { StampDocumentPreviewRequest } from '@app/types/api/models/stamp-document-preview-request';
import { EnvConfig } from '@app/types/environments';

/**
 * A response object for the ClerkReviewDocument API endpoint.
 */
export interface ClerkReviewDocumentResponse {
  data: ClerkReviewDocument[];
  total: number;
  skip: number;
  take: number;
  sortBy: string;
  sortOrder: SortOrder;
}

/**
 * A response object for the ClerkReviewParty API endpoint.
 */
export interface ClerkReviewPartyResponse {
  data: ClerkReviewParty[];
  total: number;
  skip: number;
  take: number;
  sortBy: string;
  sortOrder: SortOrder;
}

/**
 * A service containing the ClerkReview API endpoints.
 */
@Injectable({ providedIn: 'root' })
export class ClerkReviewApiService {
  /**
   * The Angular HttpClient service used to make the API calls.
   */
  private readonly http = inject(HttpClient);

  /**
   * The injected environment configuration used to set the base URL for the API calls.
   */
  private readonly envConfig = inject<Observable<EnvConfig>>(ENV_CONFIG);

  /**
   * A public api method to allow for the retrieval of the ClerkReviewFilingSummary objects.
   * 
   * @param payload An IClerkReviewFilterPayload object containing the required filter.
   * 
   * @param includeSettings A boolean indicating whether to include the settings in the response.
   * 
   * @returns The ClerkReviewListResponse object containing the ClerkReviewFilingSummary 
   * objects and related paging data.
   */
  getClerkReviewItemSummaries(payload: ClerkReviewQueryRequest, includeSettings = true): Observable<ClerkReviewListResponse> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.post(
          `${envConfig.ApiServer.BaseURL}/clerk-review/query`,
          { ...payload, includeSettings: includeSettings }
        );
      })
    );
  }

  /**
   * A public api method to get a Clerk Review Filing object.
   * 
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve the Clerk Review Filing object for.
   * 
   * @returns The Clerk Review Filing object for the given filingUniqueId.
   */
  getClerkReviewData(filingUniqueId: string): Observable<ClerkReviewFiling> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.get<ClerkReviewFiling>(
          `${envConfig.ApiServer.BaseURL}/clerk-review/filing/${filingUniqueId}`
        );
      })
    );
  }

  /**
   * Retrieves the full, uncapped SERVICE-role recipient list for a filing. The main
   * getClerkReviewData response caps recipients for page-load performance; this backs
   * the "View All Recipients" sidecar.
   *
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve recipients for.
   *
   * @returns The full recipient list grouped by case.
   */
  getClerkReviewRecipients(filingUniqueId: string): Observable<ClerkReviewRecipientsResponse> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.get<ClerkReviewRecipientsResponse>(
          `${envConfig.ApiServer.BaseURL}/clerk-review/filing/${filingUniqueId}/recipients`
        );
      })
    );
  }

  /**
   * A public api method to reset a Clerk Review Filing object to its initial state..
   * 
   * @param filingUniqueId The filingUniqueId (GUID) to reset the Clerk Review Filing object for.
   * 
   * @returns The Clerk Review Filing object for the given filingUniqueId in the original state.
   */
  resetClerkReviewData(filingUniqueId: string): Observable<ClerkReviewFiling> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.post<ClerkReviewFiling>(
          `${envConfig.ApiServer.BaseURL}/clerk-review/filing/${filingUniqueId}`,
          null
        );
      })
    );
  }

  /**
   * A public api method to patch a Clerk Review Filing Object.
   * 
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve the Clerk Review Data object for.
   * @param patchData The patch data to be applied to the Clerk Review Data object.
   * 
   * @returns The Clerk Review Data object after patching
   */
  patchClerkReviewFiling(filingUniqueId: string, patchData: any): Observable<ClerkReviewFiling> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.patch<ClerkReviewFiling>(
          `${envConfig.ApiServer.BaseURL}/clerk-review/filing/${filingUniqueId}`,
          patchData,
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json-patch+json',
            }),
          }
        );
      })
    );
  }

  /**
   * A public api method to delete a Clerk Review Filing original and modified state on the server.
   * 
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve the Clerk Review Data object for.
   * 
   * @returns The Clerk Review Data object after patching
   */
  deleteClerkReviewFiling(filingUniqueId: string): Observable<ClerkReviewFiling> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.delete<ClerkReviewFiling>(
          `${envConfig.ApiServer.BaseURL}/clerk-review/filing/${filingUniqueId}/patch-delete`
        );
      })
    );
  }

  /**
   * A public api method to submit a Clerk Review Filing Object.
   *
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve the Clerk Review Data object for.
   *
   * @returns The filing's payment status right after submission, or null both when the
   * response body is empty (the filing never needed payment capture) and when the request
   * itself has no body to parse.
   */
  submitClerkReviewFiling(filingUniqueId: string): Observable<SubmitClerkReviewResult | null> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.post<SubmitClerkReviewResult | null>(
          `${envConfig.ApiServer.BaseURL}/clerk-review/filing/${filingUniqueId}/patch-submit`,
          null
        );
      })
    );
  }

  /**
  * A public api method to request stamped document preview.
  *
  * @param documentId The document ID.
  * @param stampId The stamp ID.
  *
  * @returns The stamped document as a base64 encoded string.
  */
  requestStampedDocumentPreview(request: StampDocumentPreviewRequest): Observable<string> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) => {
        return this.http.post(
          `${envConfig.ApiServer.BaseURL}/clerk-review/document/stamp/preview`,
          request,
          { responseType: 'text' }
        );
      })
    );
  }

  /**
   * Sends the filing to one of its current queue's escalation queues.
   *
   * @param filingUniqueId The filing GUID under review.
   * @param request The selected queue and optional reason.
   */
  escalateFiling(
    filingUniqueId: string,
    request: EscalateClerkReviewFilingRequest,
  ): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.post<void>(
          `${envConfig.ApiServer.BaseURL}/clerk-review/filing/${filingUniqueId}/escalate`,
          request,
        );
      }),
    );
  }

  /**
   * A public api method to get a Clerk Review Filing Configuration object.
   *
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve the Clerk Review Configuration object for.
   *
   * @returns The Clerk Review Configuration object for the given filingUniqueId.
   */
  getClerkReviewConfiguration(filingUniqueId: string): Observable<ClerkReviewFilingConfiguration> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.get<ClerkReviewFilingConfiguration>(
          `${envConfig.ApiServer.BaseURL}/clerk-review/filing/${filingUniqueId}/configuration`
        );
      })
    );
  }

}
